import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Search, User, Calendar, MapPin, Clock, Hash, Loader2, FileDown, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Clients Data Entry — searchable directory of every birth profile the
 * studio has entered. Search by name, DOB, TOB, place, or profile ID.
 * Clicking a card opens that profile as the "active" one in the main
 * dashboard (via the onOpen callback the parent supplies).
 */
export default function ClientsPanel({ onOpenProfile }) {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [photoBump, setPhotoBump] = useState(0);   // for cache-busting <img>

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const list = await api.listProfiles();
        if (alive) setProfiles(list || []);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return profiles;
    return profiles.filter((p) =>
      (p.name || "").toLowerCase().includes(t)
      || (p.dob || "").toLowerCase().includes(t)
      || (p.tob || "").toLowerCase().includes(t)
      || (p.place_name || "").toLowerCase().includes(t)
      || (p.profile_id || "").toLowerCase().includes(t)
    );
  }, [q, profiles]);

  const [downloading, setDownloading] = useState(null);
  const [sharing, setSharing] = useState(null);
  const [langs, setLangs] = useState({});
  const [coverage, setCoverage] = useState({});

  useEffect(() => {
    fetch(`${API}/api/report-coverage`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { counts: {} }))
      .then((d) => setCoverage(d.counts || {}))
      .catch(() => {});
  }, []);
  const langOf = (p) => langs[p.profile_id] || "both";

  const shareWhatsApp = async (p) => {
    const digits = (p.phone || "").replace(/\D/g, "");
    if (!digits) { toast.error("No WhatsApp number saved for this client"); return; }
    setSharing(p.profile_id);
    try {
      const res = await fetch(
        `${API}/api/profiles/${p.profile_id}/master-horoscope/share?lang=${langOf(p)}`,
        { method: "POST", credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const d = await res.json();
      const msg = `Namaste ${p.name || ""}, your Master Horoscope report is ready.\n`
        + `Download it here (valid for 7 days):\n${API}${d.path}\n\n— TEJA ASTROLOGY`;
      window.open(`https://wa.me/${digits}?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
    } catch (e) {
      toast.error(`Could not create the share link (${e.message})`);
    } finally { setSharing(null); }
  };

  const downloadMasterPdf = async (p) => {
    setDownloading(p.profile_id);
    try {
      const res = await fetch(`${API}/api/profiles/${p.profile_id}/master-horoscope/pdf?lang=${langOf(p)}`,
                              { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = `master_horoscope_${(p.name || "client").replace(/\W+/g, "_")}_${langOf(p)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Master Horoscope downloaded");
    } catch (e) {
      toast.error(`Could not build the report (${e.message})`);
    } finally { setDownloading(null); }
  };

  const openProfile = (p) => {
    if (typeof onOpenProfile === "function") {
      onOpenProfile(p);
    }
  };

  return (
    <div className="space-y-5" data-testid="clients-panel">
      {/* header */}
      <div className="border-2 border-[#B08A3E] rounded-md p-5 bg-gradient-to-br from-[#FBF3DC] to-[#F0E6C8] shadow-md">
        <div className="flex items-center gap-2 mb-1">
          <User size={18} className="text-[#8B4513]" />
          <div className="text-[10px] uppercase tracking-[0.4em] font-mono text-[#8B4513]">
            Clients Data Entry
          </div>
        </div>
        <div className="font-heading text-2xl text-[#3A2A0F] mb-2">
          {profiles.length} client{profiles.length === 1 ? "" : "s"} on file
        </div>
        <div className="text-xs font-mono text-[#5B4A2A] leading-relaxed">
          Searchable directory of every birth profile ever entered.
          Click any card to open that native's full astrology dashboard.
        </div>
      </div>

      {/* search */}
      <div className="flex items-center gap-2 border border-[#B08A3E]/50 rounded-sm px-3 py-2 bg-white/70">
        <Search size={14} className="text-[#8B4513]" />
        <input value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="Search by name, DOB, time, place, or profile ID…"
               data-testid="clients-search"
               className="bg-transparent w-full text-sm text-[#3A2A0F] focus:outline-none placeholder:text-[#8A6E3B] font-mono" />
        {q && (
          <button onClick={() => setQ("")}
                  className="text-[9px] uppercase tracking-widest font-mono text-[#8B4513] hover:text-[#3A2A0F]">
            Clear
          </button>
        )}
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-[#8B4513] font-mono text-sm py-6">
          <Loader2 size={14} className="animate-spin" /> Loading clients…
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="italic text-sm text-[#5B4A2A] px-2 py-6 border border-dashed border-[#B08A3E]/50 rounded-sm text-center">
          {profiles.length === 0
            ? "No clients yet — add a birth profile from the sidebar."
            : `No client matches "${q}".`}
        </div>
      )}

      <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map((p) => (
          <div key={p.profile_id} className="flex flex-col gap-1">
          <button onClick={() => openProfile(p)}
                  data-testid={`client-card-${p.profile_id}`}
                  className="text-left border-2 border-[#B08A3E]/60 hover:border-[#8B4513] rounded-md p-3 bg-white/80 hover:bg-[#FDF7E4] shadow-sm hover:shadow-md transition-all flex gap-3">
            <div className="w-16 h-16 rounded-md overflow-hidden border border-[#B08A3E]/40 bg-[#F0E6C8] flex items-center justify-center shrink-0">
              <img src={`${API}/api/profiles/${p.profile_id}/photo?v=${photoBump}`}
                    alt={p.name}
                    onError={(e) => { e.target.style.display = "none"; }}
                    className="w-full h-full object-cover" />
              <User size={22} className="text-[#B08A3E] absolute" />
            </div>
            <div className="flex-1 min-w-0 space-y-1">
              <div className="font-heading text-base text-[#3A2A0F] truncate">
                {p.name || "(unnamed)"}
              </div>
              <div className="flex items-center gap-1 text-[10px] font-mono text-[#5B4A2A]">
                <Calendar size={10} /> {p.dob || "—"}
                <Clock size={10} className="ml-2" /> {p.tob || "—"}
              </div>
              <div className="flex items-center gap-1 text-[10px] font-mono text-[#5B4A2A] truncate">
                <MapPin size={10} /> {p.place_name || "—"}
              </div>
              <div className="flex items-center gap-1 text-[9px] font-mono text-[#8B4513] uppercase tracking-widest truncate">
                <Hash size={9} /> {p.profile_id}
              </div>
              <div data-testid={`client-coverage-${p.profile_id}`}
                   className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-mono uppercase tracking-widest ${
                     coverage[p.profile_id] ? "bg-[#1F8A4C]/15 text-[#1F7A44]" : "bg-[#B0413E]/10 text-[#B0413E]"}`}>
                {coverage[p.profile_id] || 0} readings ready for report
              </div>
            </div>
          </button>
          <div className="flex gap-1">
            {[["en", "English"], ["te", "Telugu"], ["both", "Both"]].map(([v, l]) => (
              <button key={v} type="button"
                      onClick={() => setLangs((m) => ({ ...m, [p.profile_id]: v }))}
                      data-testid={`client-pdf-lang-${v}-${p.profile_id}`}
                      className={`flex-1 rounded-sm border py-1 text-[9px] uppercase tracking-widest font-mono transition-colors ${
                        langOf(p) === v
                          ? "bg-[#3A2A0F] text-[#FBF3DC] border-[#3A2A0F]"
                          : "bg-white/70 text-[#8B4513] border-[#B08A3E]/50 hover:bg-[#FDF7E4]"}`}>
                {l}
              </button>
            ))}
          </div>
          <button onClick={() => downloadMasterPdf(p)}
                  disabled={downloading === p.profile_id}
                  data-testid={`client-master-pdf-${p.profile_id}`}
                  className="flex items-center justify-center gap-1.5 border border-[#8B4513]/60 rounded-sm py-1.5 text-[10px] uppercase tracking-widest font-mono text-[#8B4513] bg-[#FBF3DC] hover:bg-[#8B4513] hover:text-[#FBF3DC] disabled:opacity-60 transition-colors">
            {downloading === p.profile_id
              ? <><Loader2 size={11} className="animate-spin" /> Compiling report…</>
              : <><FileDown size={11} /> Download Full Horoscope Report</>}
          </button>
          <button onClick={() => shareWhatsApp(p)}
                  disabled={sharing === p.profile_id}
                  data-testid={`client-whatsapp-share-${p.profile_id}`}
                  title={p.phone ? `Send to ${p.phone}` : "No WhatsApp number saved"}
                  className="flex items-center justify-center gap-1.5 border border-[#1F8A4C]/60 rounded-sm py-1.5 text-[10px] uppercase tracking-widest font-mono text-[#1F7A44] bg-[#EAF6EE] hover:bg-[#1F8A4C] hover:text-white disabled:opacity-60 transition-colors">
            {sharing === p.profile_id
              ? <><Loader2 size={11} className="animate-spin" /> Preparing link…</>
              : <><MessageCircle size={11} /> Send Report on WhatsApp</>}
          </button>
          </div>
        ))}
      </div>
    </div>
  );
}
